/**
 * useDataGridClipboard
 *
 * Handles copy, cut, paste (single-cell instant + multi-cell with preview dialog).
 * Multi-cell paste parses TSV (tab-separated values from Excel/Sheets),
 * shows a preview dialog, and batch-saves via frappe.client.bulk_update.
 *
 * Plan 124, Epic 04: Range-aware copy/cut/paste/fill.
 */

import type React from 'react';
import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { frappe } from '@/lib/frappe-client';
import type {
  CellPosition,
  CellEdit,
  CellRange,
  NormalizedRect,
  UndoEntry,
  PastePreview,
} from './types';
import { isMultiCellRange } from './dataGridRangeKeyboardHelpers';

const BATCH_SIZE = 50;

export interface UseDataGridClipboardOptions {
  activeCell: CellPosition | null;
  getRowData: (rowIndex: number) => Record<string, unknown> | null;
  saveCell: (edit: CellEdit) => Promise<boolean>;
  pushUndoEntry: (entry: UndoEntry) => void;
  doctype: string;
  rowCount: number;
  /** Label for the active column (for paste dialog title) */
  getFieldLabel: (fieldname: string) => string;
  /** Shared ref — set to true during paste, read by undo hook to block */
  isPastingRef: React.RefObject<boolean>;
  // Range selection (Plan 124, Epic 04)
  selectionRange: CellRange | null;
  normalizedRect: NormalizedRect | null;
  visibleColumnIds: string[];
  editableColumnIds: string[];
}

export interface UseDataGridClipboardReturn {
  copy: () => void;
  cut: () => void;
  paste: () => void;
  pastePreview: PastePreview | null;
  isPasting: boolean;
  applyPaste: () => Promise<void>;
  cancelPaste: () => void;
  pasteProgress: { current: number; total: number } | null;
  /** True for ~200ms after a range copy — used for visual flash */
  copyFlashActive: boolean;
}

export function useDataGridClipboard(
  opts: UseDataGridClipboardOptions,
): UseDataGridClipboardReturn {
  const queryClient = useQueryClient();
  const [pastePreview, setPastePreview] = useState<PastePreview | null>(null);
  const [isPasting, setIsPasting] = useState(false);
  const [pasteProgress, setPasteProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [copyFlashActive, setCopyFlashActive] = useState(false);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ----- Helper: build TSV from range -----
  const buildRangeTSV = useCallback((): string | null => {
    const { normalizedRect, visibleColumnIds } = opts;
    if (!normalizedRect) return null;
    const lines: string[] = [];
    for (let r = normalizedRect.minRow; r <= normalizedRect.maxRow; r++) {
      const row = opts.getRowData(r);
      const cells: string[] = [];
      for (let c = normalizedRect.minColIdx; c <= normalizedRect.maxColIdx; c++) {
        const colId = visibleColumnIds[c];
        cells.push(String(row?.[colId] ?? ''));
      }
      lines.push(cells.join('\t'));
    }
    return lines.join('\n');
  }, [opts]);

  // ----- Helper: trigger copy flash -----
  const triggerCopyFlash = useCallback(() => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setCopyFlashActive(true);
    flashTimerRef.current = setTimeout(() => setCopyFlashActive(false), 200);
  }, []);

  // ----- Copy -----
  const copy = useCallback(() => {
    const { selectionRange } = opts;

    // Range copy
    if (isMultiCellRange(selectionRange) && opts.normalizedRect) {
      const tsv = buildRangeTSV();
      if (!tsv) return;
      navigator.clipboard.writeText(tsv).catch(() => {});
      triggerCopyFlash();
      return;
    }

    // Single-cell copy
    if (!opts.activeCell) return;
    const row = opts.getRowData(opts.activeCell.rowIndex);
    if (!row) return;
    const value = row[opts.activeCell.columnId];
    navigator.clipboard.writeText(String(value ?? '')).catch(() => {});
  }, [opts, buildRangeTSV, triggerCopyFlash]);

  // ----- Cut -----
  const cut = useCallback(async () => {
    const { selectionRange, normalizedRect, visibleColumnIds, editableColumnIds } = opts;

    // Range cut
    if (isMultiCellRange(selectionRange) && normalizedRect) {
      const tsv = buildRangeTSV();
      if (!tsv) return;
      try {
        await navigator.clipboard.writeText(tsv);
      } catch {
        return;
      }

      const batchId = Date.now().toString(36);
      for (let r = normalizedRect.minRow; r <= normalizedRect.maxRow; r++) {
        const row = opts.getRowData(r);
        if (!row) continue;
        const name = row.name as string;
        if (!name) continue;
        for (let c = normalizedRect.minColIdx; c <= normalizedRect.maxColIdx; c++) {
          const colId = visibleColumnIds[c];
          if (!editableColumnIds.includes(colId)) continue;
          const oldValue = row[colId];
          const success = await opts.saveCell({
            doctype: opts.doctype,
            name,
            fieldname: colId,
            value: '',
            oldValue,
          });
          if (success) {
            opts.pushUndoEntry({
              doctype: opts.doctype,
              name,
              fieldname: colId,
              oldValue,
              newValue: '',
              timestamp: Date.now(),
              batchId,
            });
          }
        }
      }
      return;
    }

    // Single-cell cut (unchanged)
    if (!opts.activeCell) return;
    const row = opts.getRowData(opts.activeCell.rowIndex);
    if (!row) return;
    const name = row.name as string;
    if (!name) return;
    const fieldname = opts.activeCell.columnId;
    const oldValue = row[fieldname];

    try {
      await navigator.clipboard.writeText(String(oldValue ?? ''));
    } catch {
      return;
    }

    const success = await opts.saveCell({
      doctype: opts.doctype,
      name,
      fieldname,
      value: '',
      oldValue,
    });
    if (success) {
      opts.pushUndoEntry({
        doctype: opts.doctype,
        name,
        fieldname,
        oldValue,
        newValue: '',
        timestamp: Date.now(),
      });
    }
  }, [opts, buildRangeTSV]);

  // ----- Paste -----
  const paste = useCallback(async () => {
    if (!opts.activeCell) return;

    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      toast.error('Cannot read clipboard. Make sure you are on HTTPS.');
      return;
    }

    if (!text) return;

    // Parse TSV: strip \r, split by newlines, split by tabs
    const lines = text.replace(/\r/g, '').split('\n').filter(Boolean);
    const parsed = lines.map((line) => line.split('\t'));

    const { selectionRange, normalizedRect, visibleColumnIds, editableColumnIds } = opts;
    const hasRange = isMultiCellRange(selectionRange) && normalizedRect;

    // ── Single-value fill into range ──
    if (hasRange && parsed.length === 1 && parsed[0].length === 1) {
      const fillValue = parsed[0][0];
      const columns: PastePreview['columns'] = [];
      const grid: PastePreview['grid'] = [];

      // Build column headers for editable columns in range
      const editableColsInRange: Array<{ colIdx: number; colId: string }> = [];
      for (let c = normalizedRect.minColIdx; c <= normalizedRect.maxColIdx; c++) {
        const colId = visibleColumnIds[c];
        if (editableColumnIds.includes(colId)) {
          editableColsInRange.push({ colIdx: c, colId });
          columns.push({ fieldname: colId, fieldLabel: opts.getFieldLabel(colId) });
        }
      }
      if (columns.length === 0) return;

      for (let r = normalizedRect.minRow; r <= normalizedRect.maxRow; r++) {
        const row = opts.getRowData(r);
        if (!row) continue;
        const name = row.name as string;
        if (!name) continue;
        const gridRow: Array<{ name: string; rowIndex: number; currentValue: unknown; newValue: string }> = [];
        for (const { colId } of editableColsInRange) {
          gridRow.push({
            name,
            rowIndex: r,
            currentValue: row[colId],
            newValue: fillValue,
          });
        }
        grid.push(gridRow);
      }

      if (grid.length === 0) return;
      setPastePreview({
        fieldname: columns[0].fieldname,
        fieldLabel: columns[0].fieldLabel,
        rows: [],
        columns,
        grid,
      });
      return;
    }

    // ── 2D paste (multi-column) ──
    if (parsed.length > 0 && parsed[0].length > 1) {
      const anchor = opts.activeCell;
      const anchorColIdx = visibleColumnIds.indexOf(anchor.columnId);
      if (anchorColIdx < 0) return;

      const numParsedCols = parsed[0].length;
      const columns: PastePreview['columns'] = [];
      const targetColIndices: number[] = [];

      for (let ci = 0; ci < numParsedCols; ci++) {
        const colIdx = anchorColIdx + ci;
        if (colIdx >= visibleColumnIds.length) break;
        const colId = visibleColumnIds[colIdx];
        targetColIndices.push(colIdx);
        columns.push({ fieldname: colId, fieldLabel: opts.getFieldLabel(colId) });
      }
      if (columns.length === 0) return;

      const grid: PastePreview['grid'] = [];
      const maxRows = Math.min(parsed.length, opts.rowCount - anchor.rowIndex);

      for (let ri = 0; ri < maxRows; ri++) {
        const rowIdx = anchor.rowIndex + ri;
        const row = opts.getRowData(rowIdx);
        if (!row) continue;
        const name = row.name as string;
        if (!name) continue;
        const gridRow: Array<{ name: string; rowIndex: number; currentValue: unknown; newValue: string }> = [];
        for (let ci = 0; ci < columns.length; ci++) {
          const colId = columns[ci].fieldname;
          gridRow.push({
            name,
            rowIndex: rowIdx,
            currentValue: row[colId],
            newValue: parsed[ri]?.[ci] ?? '',
          });
        }
        grid.push(gridRow);
      }

      if (grid.length === 0) return;
      setPastePreview({
        fieldname: columns[0].fieldname,
        fieldLabel: columns[0].fieldLabel,
        rows: [],
        columns,
        grid,
      });
      return;
    }

    // ── Single-cell paste: 1 row, 1 column — instant ──
    if (parsed.length === 1 && parsed[0].length === 1) {
      const row = opts.getRowData(opts.activeCell.rowIndex);
      if (!row) return;
      const name = row.name as string;
      if (!name) return;
      const fieldname = opts.activeCell.columnId;
      const oldValue = row[fieldname];
      const newValue = parsed[0][0];

      const success = await opts.saveCell({
        doctype: opts.doctype,
        name,
        fieldname,
        value: newValue,
        oldValue,
      });
      if (success) {
        opts.pushUndoEntry({
          doctype: opts.doctype,
          name,
          fieldname,
          oldValue,
          newValue,
          timestamp: Date.now(),
        });
      }
      return;
    }

    // ── Multi-row single-column paste (existing behavior) ──
    const fieldname = opts.activeCell.columnId;
    const startRow = opts.activeCell.rowIndex;
    const values = parsed.map((row) => row[0] ?? '');
    const maxRows = Math.min(values.length, opts.rowCount - startRow);

    const rows: PastePreview['rows'] = [];
    for (let i = 0; i < maxRows; i++) {
      const row = opts.getRowData(startRow + i);
      if (!row) continue;
      const name = row.name as string;
      if (!name) continue;
      rows.push({
        name,
        rowIndex: startRow + i,
        currentValue: row[fieldname],
        newValue: values[i],
      });
    }

    if (rows.length === 0) return;

    setPastePreview({
      fieldname,
      fieldLabel: opts.getFieldLabel(fieldname),
      rows,
    });
  }, [opts]);

  // ----- Apply paste (after dialog confirmation) -----
  const applyPaste = useCallback(async () => {
    if (!pastePreview) return;

    setIsPasting(true);
    (opts.isPastingRef as React.MutableRefObject<boolean>).current = true;

    try {
      // ── 2D / fill paste ──
      if (pastePreview.columns && pastePreview.grid) {
        const { columns, grid } = pastePreview;
        const batchId = Date.now().toString(36);
        const total = grid.length;
        setPasteProgress({ current: 0, total });

        // Group cells by row (merge multiple fields per row into one bulk_update object)
        for (let i = 0; i < grid.length; i += BATCH_SIZE) {
          const batchRows = grid.slice(i, i + BATCH_SIZE);
          const docs: Array<Record<string, unknown>> = [];

          for (const gridRow of batchRows) {
            if (gridRow.length === 0) continue;
            const doc: Record<string, unknown> = {
              doctype: opts.doctype,
              docname: gridRow[0].name,
            };
            for (let ci = 0; ci < gridRow.length; ci++) {
              const colId = columns[ci].fieldname;
              // Skip readonly columns
              if (opts.editableColumnIds.includes(colId)) {
                doc[colId] = gridRow[ci].newValue;
              }
            }
            // Only include if at least one editable field
            if (Object.keys(doc).length > 2) { // doctype + docname + at least 1 field
              docs.push(doc);
            }
          }

          if (docs.length > 0) {
            const result = await frappe.call<{ failed_docs?: string[] }>(
              'frappe.client.bulk_update',
              { docs: JSON.stringify(docs) },
            );
            const failed = new Set(result?.failed_docs ?? []);

            // Push undo entries for successful updates (one per cell)
            for (const gridRow of batchRows) {
              if (gridRow.length === 0) continue;
              const rowName = gridRow[0].name;
              if (failed.has(rowName)) continue;
              for (let ci = 0; ci < gridRow.length; ci++) {
                const colId = columns[ci].fieldname;
                if (!opts.editableColumnIds.includes(colId)) continue;
                opts.pushUndoEntry({
                  doctype: opts.doctype,
                  name: rowName,
                  fieldname: colId,
                  oldValue: gridRow[ci].currentValue,
                  newValue: gridRow[ci].newValue,
                  timestamp: Date.now(),
                  batchId,
                });
              }
            }

            if (failed.size > 0) {
              toast.error(`${failed.size} rows failed to update.`);
            }
          }

          setPasteProgress({ current: Math.min(i + BATCH_SIZE, total), total });
        }

        queryClient.invalidateQueries({ queryKey: ['doctype-list', opts.doctype] });
        return;
      }

      // ── Single-column paste (existing behavior) ──
      const { fieldname, rows } = pastePreview;
      const total = rows.length;
      setPasteProgress({ current: 0, total });

      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        const docs = batch.map((r) => ({
          doctype: opts.doctype,
          docname: r.name,
          [fieldname]: r.newValue,
        }));

        const result = await frappe.call<{ failed_docs?: string[] }>(
          'frappe.client.bulk_update',
          { docs: JSON.stringify(docs) },
        );

        const failed = new Set(result?.failed_docs ?? []);
        for (const r of batch) {
          if (!failed.has(r.name)) {
            opts.pushUndoEntry({
              doctype: opts.doctype,
              name: r.name,
              fieldname,
              oldValue: r.currentValue,
              newValue: r.newValue,
              timestamp: Date.now(),
            });
          }
        }

        if (failed.size > 0) {
          toast.error(`${failed.size} rows failed to update.`);
        }

        setPasteProgress({ current: Math.min(i + BATCH_SIZE, total), total });
      }

      queryClient.invalidateQueries({ queryKey: ['doctype-list', opts.doctype] });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Paste failed.';
      toast.error(message);
    } finally {
      setIsPasting(false);
      (opts.isPastingRef as React.MutableRefObject<boolean>).current = false;
      setPastePreview(null);
      setPasteProgress(null);
    }
  }, [pastePreview, opts, queryClient]);

  const cancelPaste = useCallback(() => {
    setPastePreview(null);
    setPasteProgress(null);
  }, []);

  return {
    copy,
    cut,
    paste,
    pastePreview,
    isPasting,
    applyPaste,
    cancelPaste,
    pasteProgress,
    copyFlashActive,
  };
}
